import React, { useRef, useState, useEffect, useDeferredValue } from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useMotionTemplate,
  animate as fmAnimate,
} from 'framer-motion';
import {
  AlertCircle,
  Building2,
  ChevronRight,
  Clock3,
  Filter,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { http } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useDeviceType } from '../../hooks/useDeviceType';
import ClienteForm from '../clientes/ClienteForm';
import ResponsiveModal from '../ui/ResponsiveModal';

// ── Helpers ───────────────────────────────────────────────────────────────────
const getClienteId = (c) => c?._id || c?.id || '';
const formatDate = (v) => {
  if (!v) return 'N/A';
  try { return new Date(v).toLocaleDateString('es-PY', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return 'N/A'; }
};
const getActiveFiltersCount = (f) => Object.values(f).filter(Boolean).length;

const STATUS_MAP = {
  activo:     { bar: 'bg-emerald-400', dot: 'bg-emerald-400', badge: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300' },
  inactivo:   { bar: 'bg-slate-500',   dot: 'bg-slate-500',   badge: 'border-slate-500/25  bg-slate-500/10  text-slate-400'    },
  suspendido: { bar: 'bg-rose-500',    dot: 'bg-rose-500',    badge: 'border-rose-500/25   bg-rose-500/10   text-rose-300'      },
  prospecto:  { bar: 'bg-amber-400',   dot: 'bg-amber-400',   badge: 'border-amber-400/25  bg-amber-400/10  text-amber-300'     },
};
const getStatus = (e) => STATUS_MAP[e] || STATUS_MAP.inactivo;

const TIPO_MAP = {
  empresa:    'border-cyan-400/25 bg-cyan-400/10 text-cyan-300',
  gobierno:   'border-violet-400/25 bg-violet-400/10 text-violet-300',
  particular: 'border-amber-400/25 bg-amber-400/10 text-amber-300',
};
const getTipoBadge = (t) => TIPO_MAP[t] || 'border-slate-600/25 bg-slate-600/10 text-slate-400';

// ── CSS: Border Beam keyframe ─────────────────────────────────────────────────
const NEXUS_CSS = `
  @keyframes beam-travel { to { stroke-dashoffset: -9999; } }
  .beam-glow  { animation: beam-travel 3s linear infinite; }
  .beam-sharp { animation: beam-travel 3s linear infinite 0.15s; }
`;

// ── BorderBeam – inspired by MagicUI ─────────────────────────────────────────
function BorderBeam({ duration = 3 }) {
  return (
    <svg
      className="pointer-events-none absolute inset-[1px]"
      width="100%" height="100%"
      style={{ overflow: 'visible' }}
    >
      {/* Glow halo */}
      <rect x="0" y="0" width="100%" height="100%" rx="13"
        fill="none" stroke="#22d3ee" strokeWidth="6" strokeOpacity="0.15"
        strokeDasharray="100 9999"
        className="beam-glow"
        style={{ animationDuration: `${duration}s` }}
      />
      {/* Sharp beam */}
      <rect x="0" y="0" width="100%" height="100%" rx="13"
        fill="none" stroke="#22d3ee" strokeWidth="1.5" strokeOpacity="0.9"
        strokeDasharray="60 9999"
        className="beam-sharp"
        style={{ animationDuration: `${duration}s` }}
      />
    </svg>
  );
}

// ── AnimatedNumber – counter animation ───────────────────────────────────────
function AnimatedNumber({ value, suffix = '' }) {
  const spanRef = useRef(null);
  const motionVal = useMotionValue(0);

  useEffect(() => {
    const target = typeof value === 'number' ? value : 0;
    const unsubscribe = motionVal.on('change', (v) => {
      if (spanRef.current) spanRef.current.textContent = Math.round(v) + suffix;
    });
    const controls = fmAnimate(motionVal, target, { duration: 1.4, ease: 'easeOut' });
    return () => { controls.stop(); unsubscribe(); };
  }, [value, suffix, motionVal]);

  return <span ref={spanRef}>0{suffix}</span>;
}

// ── StatusDot ─────────────────────────────────────────────────────────────────
function StatusDot({ estado }) {
  const s = getStatus(estado || 'activo');
  return (
    <span className="relative inline-flex h-3 w-3 flex-shrink-0 items-center justify-center">
      <motion.span
        className={`absolute inset-0 rounded-full ${s.dot} opacity-60`}
        animate={{ scale: [1, 2.4, 1], opacity: [0.6, 0, 0.6] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <span className={`relative h-1.5 w-1.5 rounded-full ${s.dot}`} />
    </span>
  );
}

// ── Framer Motion variants for roster ────────────────────────────────────────
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04, delayChildren: 0 } },
};
const rowVariants = {
  hidden:  { opacity: 0, x: -18 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 400, damping: 28 } },
};

// ── RosterRow – Aceternity "Card Hover Effect" + Border Beam ─────────────────
function RosterRow({
  cliente, hoveredId, onHover, onHoverEnd, selected, onSelect,
  onEdit, onDelete, puedeGestionar, puedeEliminar,
}) {
  const id = getClienteId(cliente);
  const estado = cliente.estado || 'activo';
  const s = getStatus(estado);
  const initial = (cliente.nombre || '?').charAt(0).toUpperCase();
  const isHovered = hoveredId === id;

  return (
    <motion.div
      variants={rowVariants}
      className={`relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3.5 transition-colors duration-150 ${
        selected ? 'border-cyan-500/40 bg-[#0e1e2c]' : 'border-[#1a2233] bg-[#0d111a]'
      }`}
      onHoverStart={() => onHover(id)}
      onHoverEnd={onHoverEnd}
      onClick={() => onSelect(id)}
      whileTap={{ scale: 0.99 }}
    >
      {/* Aceternity layoutId hover highlight */}
      <AnimatePresence>
        {isHovered && !selected && (
          <motion.span
            className="absolute inset-0 block rounded-[inherit] bg-slate-700/20 pointer-events-none"
            layoutId="rosterHoverBg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.15, delay: 0.15 } }}
          />
        )}
      </AnimatePresence>

      {/* Border Beam on selected row */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <BorderBeam />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status accent bar */}
      <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full ${s.bar}`} />

      {/* Avatar */}
      <div className={`ml-1.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border font-mono text-base font-bold transition-colors ${
        selected
          ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-300'
          : 'border-[#1e2a3a] bg-[#141c29] text-slate-400'
      }`}>
        {initial}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusDot estado={estado} />
          <span className={`truncate text-sm font-semibold ${selected ? 'text-white' : 'text-slate-200'}`}>
            {cliente.nombre || 'Sin nombre'}
          </span>
          <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.badge}`}>
            {estado}
          </span>
          {cliente.tipo && (
            <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${getTipoBadge(cliente.tipo)}`}>
              {cliente.tipo}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
          {cliente.ruc && <span className="font-mono">RUC {cliente.ruc}</span>}
          {cliente.ciudad && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{cliente.ciudad}</span>}
          {cliente.email && <span className="flex max-w-[160px] items-center gap-0.5 truncate"><Mail className="h-3 w-3 flex-shrink-0" />{cliente.email}</span>}
          <span className="flex items-center gap-0.5"><Clock3 className="h-3 w-3" />{cliente.horas_contratadas || 0}h</span>
        </div>
      </div>

      {/* Action buttons */}
      <motion.div
        className="flex items-center gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered || selected ? 1 : 0 }}
        transition={{ duration: 0.15 }}
      >
        {puedeGestionar && (
          <motion.button
            onClick={(e) => { e.stopPropagation(); onEdit(cliente); }}
            className="rounded-lg p-1.5 text-slate-500"
            whileHover={{ color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)' }}
            whileTap={{ scale: 0.88 }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </motion.button>
        )}
        {puedeEliminar && (
          <motion.button
            onClick={(e) => { e.stopPropagation(); onDelete(cliente); }}
            className="rounded-lg p-1.5 text-slate-500"
            whileHover={{ color: '#f87171', backgroundColor: 'rgba(239,68,68,0.1)' }}
            whileTap={{ scale: 0.88 }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </motion.button>
        )}
      </motion.div>

      <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${selected ? 'text-cyan-400' : 'text-[#2a3549]'}`} />
    </motion.div>
  );
}

// ── IntelDataRow ──────────────────────────────────────────────────────────────
function IntelDataRow({ icon: Icon, label, value, sub, delay = 0 }) {
  if (!value) return null;
  return (
    <motion.div
      className="flex gap-3"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-[#0d111a] text-slate-500">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1 border-b border-[#161d2c] pb-3">
        <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.28em] text-slate-600">{label}</p>
        <p className="mt-0.5 break-all text-sm font-medium text-slate-200">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
      </div>
    </motion.div>
  );
}

// ── IntelBriefing ─────────────────────────────────────────────────────────────
function IntelBriefing({ cliente, puedeGestionar, puedeEliminar, onEditar, onEliminar, isLargeDesktop, panelRef }) {
  const estado = cliente?.estado || 'activo';
  const s = getStatus(estado);

  // Aceternity spotlight on panel
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const spotlight = useMotionTemplate`radial-gradient(280px at ${mouseX}px ${mouseY}px, rgba(34,211,238,0.07) 0%, transparent 80%)`;

  const handleMouseMove = (e) => {
    const { left, top } = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - left);
    mouseY.set(e.clientY - top);
  };

  return (
    <aside ref={panelRef} className={isLargeDesktop ? 'xl:sticky xl:top-4 xl:self-start' : ''}>
      <div
        className={`relative overflow-hidden rounded-[26px] border border-[#1a2233] bg-[#0a0e18] ${
          isLargeDesktop ? 'max-h-[calc(100vh-2.5rem)] overflow-y-auto' : ''
        }`}
        onMouseMove={handleMouseMove}
      >
        {/* Spotlight overlay */}
        <motion.div className="pointer-events-none absolute inset-0 z-10 rounded-[inherit]" style={{ background: spotlight }} />

        {/* Header */}
        <div className="relative border-b border-[#161d2c] bg-[linear-gradient(135deg,_#060c1a_0%,_#091020_55%,_#060b18_100%)] px-5 py-5">
          <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(rgba(34,211,238,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.035)_1px,transparent_1px)] [background-size:24px_24px]" />
          <div className="pointer-events-none absolute -top-8 -right-8 h-28 w-28 rounded-full bg-cyan-400/8 blur-2xl" />
          <div className="relative flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-400">
              <motion.span
                className="h-1.5 w-1.5 rounded-full bg-cyan-400"
                animate={{ scale: [1, 1.7, 1], opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.8, repeat: Infinity }}
              />
              Intel Briefing
            </span>
            <span className="font-mono text-[10px] text-slate-600">
              {cliente ? 'activo' : 'standby'}
            </span>
          </div>
        </div>

        {/* Content — AnimatePresence for smooth transitions */}
        <AnimatePresence mode="wait">
          {cliente ? (
            <motion.div
              key={getClienteId(cliente)}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } }}
              exit={{ opacity: 0, y: -10, transition: { duration: 0.18, ease: 'easeIn' } }}
              className="space-y-5 p-5"
            >
              {/* Identity block */}
              <div className="overflow-hidden rounded-2xl border border-[#1a2233] bg-[#0d111a]">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <motion.div
                      className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-[18px] border font-mono text-2xl font-bold ${s.badge}`}
                      initial={{ scale: 0.75, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                    >
                      {(cliente.nombre || '?').charAt(0).toUpperCase()}
                    </motion.div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xl font-bold leading-snug tracking-tight text-white">{cliente.nombre || 'Sin nombre'}</h4>
                      {cliente.ruc && <p className="mt-0.5 font-mono text-xs text-slate-500">RUC {cliente.ruc}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.badge}`}>
                          <StatusDot estado={estado} />
                          {estado}
                        </span>
                        {cliente.tipo && (
                          <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${getTipoBadge(cliente.tipo)}`}>
                            {cliente.tipo}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 divide-x divide-[#161d2c] border-t border-[#161d2c]">
                  {[
                    { label: 'Horas',      value: `${cliente.horas_contratadas || 0}h` },
                    { label: 'Sucursales', value: cliente.sucursales?.length || 0 },
                    { label: 'Desde',      value: formatDate(cliente.created_at) },
                  ].map((item) => (
                    <div key={item.label} className="px-3 py-3 text-center">
                      <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-slate-600">{item.label}</p>
                      <p className="mt-1 font-mono text-sm font-bold text-slate-200">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact data */}
              <div className="space-y-3">
                <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.28em] text-slate-600">Contacto &amp; Ubicación</p>
                <IntelDataRow icon={Mail}   label="Email"    value={cliente.email}    delay={0.06} />
                <IntelDataRow icon={Phone}  label="Teléfono" value={cliente.telefono} delay={0.12} />
                <IntelDataRow icon={MapPin} label="Ciudad"   value={cliente.ciudad}   sub={cliente.direccion || undefined} delay={0.18} />
              </div>

              {/* Branches */}
              {(cliente.sucursales?.length ?? 0) > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
                  <p className="mb-2 font-mono text-[9.5px] font-semibold uppercase tracking-[0.28em] text-slate-600">
                    Sucursales · {cliente.sucursales.length}
                  </p>
                  <div className="space-y-1.5">
                    {cliente.sucursales.slice(0, 6).map((suc, idx) => {
                      const nombre = typeof suc === 'string' ? suc : suc?.nombre || `Sucursal ${idx + 1}`;
                      const ciudad = typeof suc === 'object' ? suc?.ubicacion_ciudad : '';
                      return (
                        <div key={`${nombre}-${idx}`} className="flex items-center gap-2.5 rounded-xl border border-[#161d2c] bg-[#0a0e18] px-3 py-2">
                          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-400/50" />
                          <span className="text-xs font-medium text-slate-300">{nombre}</span>
                          {ciudad && <span className="ml-auto text-[11px] text-slate-600">{ciudad}</span>}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Actions */}
              {puedeGestionar && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }} className="flex gap-2.5">
                  <motion.button
                    onClick={() => onEditar(cliente)}
                    className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-[#252f44] bg-[#0f1624] px-4 py-3 text-sm font-semibold text-slate-300"
                    whileHover={{ borderColor: 'rgba(58,78,104,1)', color: 'white', scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Pencil className="h-4 w-4" />
                    Editar registro
                  </motion.button>
                  {puedeEliminar && (
                    <motion.button
                      onClick={() => onEliminar(cliente)}
                      className="flex items-center justify-center gap-2 rounded-2xl border border-rose-500/20 bg-rose-500/8 px-4 py-3 text-sm font-semibold text-rose-400"
                      whileHover={{ backgroundColor: 'rgba(239,68,68,0.15)', scale: 1.01 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </motion.button>
                  )}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center px-6 py-12 text-center"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-[20px] border border-[#1a2233] bg-[#0d111a]">
                <Building2 className="h-6 w-6 text-slate-700" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.26em] text-slate-600">Sin selección activa</p>
              <p className="mt-2 max-w-[200px] text-sm leading-relaxed text-slate-500">
                Elige un cliente del roster para abrir el briefing.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </aside>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ClientesV3Preview() {
  const { user } = useAuth();
  const deviceType = useDeviceType();
  const detailPanelRef = useRef(null);

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState({ tipo: '', estado: '', ciudad: '' });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState(null);

  const deferredBusqueda = useDeferredValue(busqueda);
  const puedeGestionar = user?.rol === 'admin' || user?.rol === 'tecnico';
  const puedeEliminar = user?.rol === 'admin';
  const isLargeDesktop = deviceType === 'desktop-large';

  // Aceternity Spotlight on header
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const spotlightBg = useMotionTemplate`radial-gradient(480px at ${mouseX}px ${mouseY}px, rgba(34,211,238,0.075) 0%, transparent 80%)`;

  const handleHeaderMouseMove = (e) => {
    const { left, top } = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - left);
    mouseY.set(e.clientY - top);
  };

  // Data
  async function cargarClientes() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/clientes', { headers: { Authorization: `Bearer ${token}` } });
      if (!Array.isArray(response.data)) throw new Error('Formato de datos inválido');
      const sorted = [...response.data].sort((a, b) =>
        (a.nombre || '').toLowerCase().localeCompare((b.nombre || '').toLowerCase())
      );
      setClientes(sorted);
      setError(null);
    } catch (err) {
      setClientes([]);
      setError(err?.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { cargarClientes(); }, []);

  const clientesFiltrados = clientes.filter((c) => {
    const q = deferredBusqueda.toLowerCase();
    const match = !q || c.nombre?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
      || c.telefono?.toLowerCase().includes(q) || c.ruc?.toLowerCase().includes(q)
      || c.direccion?.toLowerCase().includes(q);
    return match
      && (!filtros.tipo || c.tipo === filtros.tipo)
      && (!filtros.estado || (c.estado || 'activo') === filtros.estado)
      && (!filtros.ciudad || c.ciudad === filtros.ciudad);
  });

  useEffect(() => {
    if (!clientesFiltrados.length) { if (selectedClienteId !== null) setSelectedClienteId(null); return; }
    const ids = clientesFiltrados.map(getClienteId);
    if (!selectedClienteId || !ids.includes(selectedClienteId)) setSelectedClienteId(ids[0]);
  }, [clientesFiltrados, selectedClienteId]);

  const clienteSeleccionado = clientes.find((c) => getClienteId(c) === selectedClienteId) ?? null;
  const tiposCliente = [...new Set(clientes.map((c) => c.tipo).filter(Boolean))];
  const estados = [...new Set(clientes.map((c) => c.estado || 'activo').filter(Boolean))];
  const ciudades = [...new Set(clientes.map((c) => c.ciudad).filter(Boolean))];
  const filtrosActivos = getActiveFiltersCount(filtros);

  const totalActivos = clientes.filter((c) => !c.estado || c.estado === 'activo').length;
  const totalCiudades = new Set(clientes.map((c) => c.ciudad).filter(Boolean)).size;
  const totalHoras = clientes.reduce((s, c) => s + Number(c.horas_contratadas || 0), 0);

  const seleccionarCliente = (id) => {
    setSelectedClienteId(id);
    if (!isLargeDesktop) {
      window.requestAnimationFrame(() => {
        detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  async function eliminarCliente(cliente) {
    if (!window.confirm(`¿Eliminar "${cliente.nombre}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      const id = getClienteId(cliente);
      if (!id) { alert('No se pudo determinar el ID del cliente.'); return; }
      await http.delete(`/clientes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      await cargarClientes();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || 'Error al eliminar');
    }
  }

  const intel = (
    <IntelBriefing
      cliente={clienteSeleccionado}
      puedeGestionar={puedeGestionar}
      puedeEliminar={puedeEliminar}
      onEditar={(c) => { setEditando(c); setModalAbierto(true); }}
      onEliminar={eliminarCliente}
      isLargeDesktop={isLargeDesktop}
      panelRef={detailPanelRef}
    />
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: NEXUS_CSS }} />

      <div className="space-y-4">

        {/* ── COMMAND HEADER with Aceternity Spotlight ─────────────────────── */}
        <motion.header
          onMouseMove={handleHeaderMouseMove}
          className="relative overflow-hidden rounded-[28px] border border-[#1a2233] bg-[linear-gradient(135deg,_#060c1a_0%,_#0a1020_50%,_#07101e_100%)]"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Grid texture */}
          <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(rgba(34,211,238,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.04)_1px,transparent_1px)] [background-size:32px_32px]" />
          {/* Glow blobs */}
          <div className="pointer-events-none absolute -left-24 top-0 h-56 w-56 rounded-full bg-cyan-400/6 blur-3xl" />
          <div className="pointer-events-none absolute right-0 bottom-0 h-48 w-48 rounded-full bg-violet-500/6 blur-3xl" />
          {/* Spotlight */}
          <motion.div className="pointer-events-none absolute inset-0" style={{ background: spotlightBg }} />

          <div className="relative p-6 md:p-8">
            <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">

              {/* Left: title */}
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="flex flex-wrap items-center gap-2.5"
                >
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-cyan-400">
                    <motion.span
                      className="h-1.5 w-1.5 rounded-full bg-cyan-400"
                      animate={{ scale: [1, 1.7, 1], opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                    />
                    NEXUS · Clients
                  </span>
                  <span className="font-mono text-[11px] text-slate-600">v3 — framer motion</span>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18, duration: 0.4 }}>
                  <h1 className="text-3xl font-bold leading-tight tracking-tight text-white md:text-[2.15rem]">
                    Gestión de clientes
                  </h1>
                  <h1 className="text-3xl font-bold leading-tight tracking-tight text-cyan-400 md:text-[2.15rem]">
                    reinventada desde cero.
                  </h1>
                </motion.div>

                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.4 }} className="max-w-lg text-sm leading-7 text-slate-500">
                  Centro de inteligencia para cada cliente. Búsqueda instantánea, filtros en tiempo real,
                  briefing con <em className="not-italic text-slate-400">spotlight</em> y animaciones de primera clase.
                </motion.p>

                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.4 }} className="flex flex-wrap gap-3 pt-1">
                  <motion.button
                    onClick={cargarClientes}
                    className="inline-flex items-center gap-2 rounded-2xl border border-[#252f44] bg-[#0e1624] px-5 py-2.5 text-sm font-semibold text-slate-300"
                    whileHover={{ borderColor: 'rgba(58,78,104,1)', color: 'white' }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </motion.button>
                  {puedeGestionar && (
                    <motion.button
                      onClick={() => { setEditando(null); setModalAbierto(true); }}
                      className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-5 py-2.5 text-sm font-semibold text-cyan-300"
                      whileHover={{ backgroundColor: 'rgba(34,211,238,0.18)', color: 'rgb(165,243,252)' }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Plus className="h-4 w-4" />
                      Nuevo cliente
                    </motion.button>
                  )}
                </motion.div>
              </div>

              {/* Right: metric cards */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Total',    value: clientes.length, num: clientes.length,  icon: Users,       accent: 'border-cyan-400/20   bg-cyan-400/8   text-cyan-400',   delay: 0.1  },
                  { label: 'Activos',  value: totalActivos,    num: totalActivos,     icon: ShieldCheck, accent: 'border-emerald-400/20 bg-emerald-400/8 text-emerald-400', delay: 0.2  },
                  { label: 'Ciudades', value: totalCiudades,   num: totalCiudades,    icon: MapPin,      accent: 'border-violet-400/20  bg-violet-400/8  text-violet-400',  delay: 0.3  },
                  { label: 'Horas',    value: totalHoras,      num: totalHoras,       icon: Clock3,      accent: 'border-amber-400/20   bg-amber-400/8   text-amber-400',   delay: 0.4, suffix: 'h' },
                ].map(({ label, num, icon: Icon, accent, delay, suffix = '' }) => (
                  <motion.div
                    key={label}
                    className={`relative overflow-hidden rounded-2xl border p-4 ${accent}`}
                    initial={{ opacity: 0, scale: 0.88, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay, type: 'spring', stiffness: 280, damping: 24 }}
                    whileHover={{ scale: 1.05, y: -4, transition: { type: 'spring', stiffness: 400, damping: 22 } }}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className="h-4 w-4 opacity-70" />
                      <span className="font-mono text-[9px] uppercase tracking-[0.24em] opacity-60">{label}</span>
                    </div>
                    <p className="mt-3 font-mono text-3xl font-bold tracking-tight text-white">
                      <AnimatedNumber value={num} suffix={suffix} />
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.header>

        {/* ── SEARCH / FILTER BAR ──────────────────────────────────────────── */}
        <motion.div
          className="rounded-[22px] border border-[#1a2233] bg-[#0d111a] p-4"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, RUC, email, teléfono..."
                className="w-full rounded-[14px] border border-[#1e2a3a] bg-[#090d16] py-2.5 pl-10 pr-4 text-sm text-slate-200 outline-none placeholder:text-slate-600 transition focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10"
              />
            </label>
            <motion.button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className={`inline-flex flex-shrink-0 items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold ${
                mostrarFiltros || filtrosActivos > 0
                  ? 'border-cyan-500/35 bg-cyan-500/10 text-cyan-300'
                  : 'border-[#1e2a3a] bg-[#090d16] text-slate-400'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <Filter className="h-4 w-4" />
              Filtros
              {filtrosActivos > 0 && (
                <span className="rounded-full bg-cyan-400/20 px-1.5 py-0.5 font-mono text-[10px] font-bold text-cyan-300">
                  {filtrosActivos}
                </span>
              )}
            </motion.button>
            <span className="flex-shrink-0 font-mono text-xs text-slate-600">
              {clientesFiltrados.length}{clientesFiltrados.length !== clientes.length && <span className="text-slate-700"> / {clientes.length}</span>}
            </span>
          </div>

          {/* Filter chips with AnimatePresence */}
          <AnimatePresence>
            {filtrosActivos > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.22 }}
                className="overflow-hidden"
              >
                <div className="mt-3 flex flex-wrap gap-2">
                  <AnimatePresence mode="popLayout">
                    {filtros.tipo && (
                      <motion.button key="tipo"
                        initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                        onClick={() => setFiltros({ ...filtros, tipo: '' })}
                        className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300"
                      >Tipo: {filtros.tipo} <X className="h-3 w-3" /></motion.button>
                    )}
                    {filtros.estado && (
                      <motion.button key="estado"
                        initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                        onClick={() => setFiltros({ ...filtros, estado: '' })}
                        className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300"
                      >Estado: {filtros.estado} <X className="h-3 w-3" /></motion.button>
                    )}
                    {filtros.ciudad && (
                      <motion.button key="ciudad"
                        initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                        onClick={() => setFiltros({ ...filtros, ciudad: '' })}
                        className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300"
                      >Ciudad: {filtros.ciudad} <X className="h-3 w-3" /></motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Filter selects expand */}
          <AnimatePresence>
            {mostrarFiltros && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.22 }}
                className="overflow-hidden"
              >
                <div className="mt-4 grid gap-3 border-t border-[#161d2c] pt-4 md:grid-cols-3">
                  {[
                    { label: 'Tipo',   key: 'tipo',   options: tiposCliente, placeholder: 'Todos los tipos'    },
                    { label: 'Estado', key: 'estado', options: estados,      placeholder: 'Todos los estados'  },
                    { label: 'Ciudad', key: 'ciudad', options: ciudades,     placeholder: 'Todas las ciudades' },
                  ].map(({ label, key, options, placeholder }) => (
                    <label key={key} className="block">
                      <span className="mb-1.5 block font-mono text-[9.5px] font-semibold uppercase tracking-[0.26em] text-slate-600">{label}</span>
                      <select
                        value={filtros[key]}
                        onChange={(e) => setFiltros({ ...filtros, [key]: e.target.value })}
                        className="w-full rounded-[14px] border border-[#1e2a3a] bg-[#090d16] px-4 py-2.5 text-sm text-slate-300 outline-none transition focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10"
                      >
                        <option value="">{placeholder}</option>
                        {options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="rounded-2xl border border-rose-500/20 bg-rose-500/8 px-5 py-4"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-400" />
                <div>
                  <p className="font-semibold text-rose-300">Error al cargar clientes</p>
                  <p className="mt-1 text-sm text-rose-500">{error}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Intel on non-large-desktop: above roster */}
        {!isLargeDesktop && intel}

        {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
        <div className={isLargeDesktop ? 'grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]' : 'space-y-4'}>

          <section>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.06 }}
                    className="relative overflow-hidden rounded-2xl border border-[#1a2233] bg-[#0d111a] p-4"
                  >
                    <div className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-slate-800" />
                    <div className="ml-2 flex animate-pulse items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-slate-800" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-44 rounded-lg bg-slate-800" />
                        <div className="h-2.5 w-28 rounded-lg bg-[#161d2c]" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : clientesFiltrados.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-[22px] border border-dashed border-[#1a2233] px-6 py-14 text-center"
              >
                <Building2 className="mx-auto h-8 w-8 text-slate-700" />
                <p className="mt-4 font-semibold text-slate-400">Sin resultados</p>
                <p className="mt-2 text-sm text-slate-600">
                  {busqueda || filtrosActivos > 0 ? 'Ajusta la búsqueda o borra los filtros activos.' : 'No hay clientes registrados.'}
                </p>
              </motion.div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {clientesFiltrados.map((cliente) => {
                  const id = getClienteId(cliente);
                  return (
                    <RosterRow
                      key={id}
                      cliente={cliente}
                      hoveredId={hoveredRowId}
                      onHover={(rowId) => setHoveredRowId(rowId)}
                      onHoverEnd={() => setHoveredRowId(null)}
                      selected={id === selectedClienteId}
                      onSelect={seleccionarCliente}
                      onEdit={(c) => { setEditando(c); setModalAbierto(true); }}
                      onDelete={eliminarCliente}
                      puedeGestionar={puedeGestionar}
                      puedeEliminar={puedeEliminar}
                    />
                  );
                })}
              </motion.div>
            )}
          </section>

          {isLargeDesktop && intel}
        </div>
      </div>

      <ResponsiveModal
        isOpen={modalAbierto}
        onClose={() => setModalAbierto(false)}
        title={editando ? 'Editar Cliente' : 'Nuevo Cliente'}
        size="large"
      >
        <ClienteForm
          cliente={editando}
          cerrar={() => setModalAbierto(false)}
          recargar={cargarClientes}
        />
      </ResponsiveModal>
    </>
  );
}
